import React, { useMemo } from 'react';
import { translate } from '../../../Services/translation';
import {
  ExtapiInvoiceReturnRequest,
  RmaInvoice,
} from '../../../Types/MyPages/InvoicesModel';
import { groupBy } from 'lodash';
import clsx from 'clsx';

interface MyPagesRmaSummaryProps {
  form: ExtapiInvoiceReturnRequest;
  invoices: RmaInvoice[];
}

export default function MyPagesRmaSummary({
  form,
  invoices,
}: MyPagesRmaSummaryProps) {
  const groupedInvoiceRows = useMemo(
    () =>
      groupBy(
        invoices.flatMap((x) => x.rows),
        (x) => x.invoiceNo
      ),
    [invoices]
  );

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-4 md:flex-row">
        <div className="flex grow basis-0 flex-col">
          <h3>{translate('mypage.rma.reference.title')}</h3>
          <span>
            {form.firstName} {form.lastName}
          </span>
          <span>{form.phone}</span>
          <span>{form.email}</span>
        </div>
        <div className="flex grow basis-0 flex-col">
          <h3>{translate('mypage.rma.general.title')}</h3>
          <span>
            {translate('mypage.rma.is.damage.product')}{' '}
            <strong>
              {form.isDamagedProduct == 1
                ? translate('mypage.rma.yes')
                : translate('mypage.rma.no')}
            </strong>
          </span>
          <span>
            {translate('mypage.rma.been.in.contact')}{' '}
            <strong>
              {form.beenInContact == 1
                ? translate('mypage.rma.yes')
                : translate('mypage.rma.no')}
            </strong>
          </span>
          {form.beenInContact == 1 && (
            <span>
              {translate('mypage.rma.been.in.contact.with.who')}{' '}
              <strong>{form.beenInContactPerson}</strong>
            </span>
          )}
        </div>
      </div>

      <h3 className="mt-6">{translate('mypage.rma.header.summary')}</h3>
      <div className="flex flex-col rounded border border-gray-200 bg-white text-sm">
        <div className={clsx(`grid grid-cols-4 p-2`)}>
          <p className="col-span-1 overflow-hidden text-ellipsis whitespace-nowrap font-bold">
            {translate('mypage.rma.itemnumber')}
          </p>
          <p className="col-span-1 overflow-hidden text-ellipsis whitespace-nowrap font-bold">
            {translate('mypage.rma.header.description')}
          </p>
          <p className="col-span-1 overflow-hidden text-ellipsis whitespace-nowrap font-bold">
            {translate('mypage.rma.reason')}
          </p>
          <p className="col-span-1 overflow-hidden text-ellipsis whitespace-nowrap text-right font-bold">
            {translate('mypage.rma.quantity')}
          </p>
        </div>

        {Object.keys(groupedInvoiceRows).map((returnInvoice, idx) => (
          <div
            key={idx}
            className="mt-2 flex flex-col rounded border border-gray-200 bg-white text-sm"
          >
            <div className="flex content-end bg-gray-100 p-2">
              <span className="text-sm opacity-80">
                {translate('mypage.rma.invoicenumber')}: {returnInvoice}
              </span>
            </div>
            {groupedInvoiceRows[returnInvoice]?.map(
              (groupedReturnInvoice, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-4 border-t border-gray-200 p-2 odd:bg-white even:bg-gray-50"
                >
                  <p className="col-span-1">
                    {groupedReturnInvoice.articleNumber}
                  </p>
                  <p className="col-span-1">
                    {groupedReturnInvoice.description}
                  </p>
                  <p className="col-span-1">
                    {groupedReturnInvoice.reason}
                  </p>
                  <p className="col-span-1 text-right">
                    {groupedReturnInvoice.returnQuantity}
                  </p>
                </div>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
